import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import {
  applyCurrencyRecalculationBatch,
  checkCurrencyRecalculation,
  listCurrencyRecalculationGameweeks,
  previewCurrencyRecalculationBatch,
} from "../../utils/deliveryRecordsApi";

/** Pause between batches so a full gameweek never hits the database in a burst. */
const BATCH_PAUSE_MS = 300;
const ROWS_PER_PAGE = 50;

/** Pause between apply batches; longer spreads the disk writes out further. */
const APPLY_PAUSES_MS = [500, 1000, 2000, 5000];
/** Waits before retrying a failed apply batch; after the last one the run stops for a manual resume. */
const APPLY_RETRY_DELAYS_MS = [2000, 5000, 10000];
/** How long to wait while another admin's batch holds the run lock. */
const APPLY_BUSY_WAIT_MS = 3000;
const APPLY_MAX_BUSY_WAITS = 40;

const idleApply = () => ({
  status: "idle", // idle | running | stopped | done | failed
  error: null,
  notice: null,
  scanned: 0,
  inserted: 0,
  updated: 0,
  // The cursor after the last committed batch; a resume continues from here.
  afterTeamId: null,
  startedAt: null,
  finishedAt: null,
});

const OUTCOMES = ["new", "changed", "unchanged", "skipped"];

const outcomeColors = {
  new: "info",
  changed: "warning",
  unchanged: "default",
  skipped: "error",
};

const SKIP_REASONS = {
  no_snapshot: "No gameweek snapshot",
  no_gaming_hub_id: "No Gaming Hub ID",
  zero_points: "0 points, no record",
};

const emptyTotals = () => ({
  new: 0,
  changed: 0,
  unchanged: 0,
  skipped: 0,
  no_snapshot: 0,
  no_gaming_hub_id: 0,
  zero_points: 0,
});

const compactTable = {
  "& .MuiTableCell-root": { fontSize: 12, py: 0.5, px: 1 },
  "& .MuiTableCell-head": { fontWeight: 600 },
  "& .MuiChip-root": { height: 20, fontSize: 11 },
};

const gameweekLabel = (gameweek) =>
  [`GW${gameweek.number}`, gameweek.name, gameweek.status]
    .filter(Boolean)
    .join(" · ");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatSeconds = (seconds) =>
  seconds >= 60
    ? `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
    : `${Math.round(seconds)}s`;

const display = (value) =>
  value === null || value === undefined ? "—" : value;

/**
 * Read-only currency re-calculator: walks every team in the gameweek's
 * season in keyset batches and shows what each team's currency would be.
 * Nothing is written.
 */
const CurrencyRecalculatorDialog = ({ open, onClose }) => {
  const [gameweeks, setGameweeks] = useState([]);
  const [gameweeksLoading, setGameweeksLoading] = useState(false);
  const [gameweek, setGameweek] = useState(null);
  const [check, setCheck] = useState({
    loading: false,
    error: null,
    data: null,
  });
  const [run, setRun] = useState({
    status: "idle", // idle | running | stopped | done | failed
    error: null,
    scanned: 0,
    startedAt: null,
    finishedAt: null,
  });
  const [totals, setTotals] = useState(emptyTotals);
  // Every row of the run; kept in a ref so a 200k-team pass does not copy arrays per batch.
  const rowsRef = useRef([]);
  const [rowsVersion, setRowsVersion] = useState(0);
  const runIdRef = useRef(0);
  const [outcomeFilter, setOutcomeFilter] = useState("changes");
  const [teamSearch, setTeamSearch] = useState("");
  const [page, setPage] = useState(0);
  const [apply, setApply] = useState(idleApply);
  const [applyPauseMs, setApplyPauseMs] = useState(1000);
  const applyIdRef = useRef(0);
  // Which confirmation is open: "apply" before writing, "close" when leaving mid-write.
  const [confirming, setConfirming] = useState(null);

  const running = run.status === "running";
  const applying = apply.status === "running";

  useEffect(() => {
    if (!open || gameweeks.length > 0) return;
    setGameweeksLoading(true);
    listCurrencyRecalculationGameweeks()
      .then(setGameweeks)
      .catch((err) => {
        console.error("Failed to fetch gameweeks:", err);
        setCheck({
          loading: false,
          error: "Failed to load gameweeks.",
          data: null,
        });
      })
      .finally(() => setGameweeksLoading(false));
  }, [open, gameweeks.length]);

  const resetRun = () => {
    runIdRef.current += 1;
    rowsRef.current = [];
    setRowsVersion((version) => version + 1);
    setTotals(emptyTotals());
    setRun({
      status: "idle",
      error: null,
      scanned: 0,
      startedAt: null,
      finishedAt: null,
    });
    setPage(0);
  };

  const handleGameweekChange = async (value) => {
    resetRun();
    applyIdRef.current += 1;
    setApply(idleApply());
    setGameweek(value);
    setCheck({ loading: Boolean(value), error: null, data: null });
    if (!value) return;
    try {
      const data = await checkCurrencyRecalculation(value.id);
      setCheck({ loading: false, error: null, data });
    } catch (err) {
      console.error("Failed to check gameweek currency:", err);
      setCheck({
        loading: false,
        error: "Failed to check this gameweek.",
        data: null,
      });
    }
  };

  const startRun = async () => {
    resetRun();
    // A fresh preview supersedes a finished apply; a stopped or failed one
    // keeps its Resume.
    if (apply.status === "done") setApply(idleApply());
    const runId = runIdRef.current;
    const startedAt = Date.now();
    setRun({
      status: "running",
      error: null,
      scanned: 0,
      startedAt,
      finishedAt: null,
    });

    let afterTeamId = null;
    let scanned = 0;
    try {
      do {
        const batch = await previewCurrencyRecalculationBatch(
          gameweek.id,
          afterTeamId
        );
        // Stopped, restarted or another gameweek picked while this batch was in flight.
        if (runIdRef.current !== runId) return;

        rowsRef.current.push(...batch.rows);
        scanned += batch.rows.length;
        setTotals((prev) => {
          const next = { ...prev };
          for (const row of batch.rows) {
            next[row.outcome] += 1;
            if (row.skip_reason) next[row.skip_reason] += 1;
          }
          return next;
        });
        setRowsVersion((version) => version + 1);
        const scannedSoFar = scanned;
        setRun((prev) => ({ ...prev, scanned: scannedSoFar }));

        afterTeamId = batch.next_after_team_id;
        if (afterTeamId) await sleep(BATCH_PAUSE_MS);
      } while (afterTeamId && runIdRef.current === runId);

      if (runIdRef.current !== runId) return;
      setRun((prev) => ({ ...prev, status: "done", finishedAt: Date.now() }));
    } catch (err) {
      if (runIdRef.current !== runId) return;
      console.error("Currency re-calculation batch failed:", err);
      setRun((prev) => ({
        ...prev,
        status: "failed",
        finishedAt: Date.now(),
        error: `A batch failed after ${scanned.toLocaleString(
          "en-GB"
        )} teams. Run it again to retry.`,
      }));
    }
  };

  /**
   * Writes the gameweek in committed batches. A failed batch is retried from
   * the same cursor; if it keeps failing the run stops and can be resumed from
   * the last committed batch. Re-running from the start is also safe: rows
   * already written come back unchanged and are not written again.
   */
  const runApply = async ({ resume }) => {
    applyIdRef.current += 1;
    const applyId = applyIdRef.current;
    const isCurrent = () => applyIdRef.current === applyId;

    let afterTeamId = resume ? apply.afterTeamId : null;
    const counts = resume
      ? {
          scanned: apply.scanned,
          inserted: apply.inserted,
          updated: apply.updated,
        }
      : { scanned: 0, inserted: 0, updated: 0 };
    setApply((prev) => ({
      ...(resume ? prev : idleApply()),
      ...counts,
      afterTeamId,
      status: "running",
      error: null,
      notice: null,
      startedAt: resume ? prev.startedAt : Date.now(),
      finishedAt: null,
    }));

    do {
      let batch = null;
      let attempt = 0;
      let busyWaits = 0;
      while (!batch) {
        try {
          batch = await applyCurrencyRecalculationBatch(
            gameweek.id,
            afterTeamId
          );
        } catch (err) {
          if (!isCurrent()) return;
          const status = err?.response?.status;
          const message = err?.response?.data?.message;
          // Another batch holds the run lock: wait for it rather than count a failure.
          if (status === 409 && /retry shortly/.test(message ?? "")) {
            busyWaits += 1;
            if (busyWaits <= APPLY_MAX_BUSY_WAITS) {
              setApply((prev) => ({
                ...prev,
                notice: "Waiting for another re-calculation batch…",
              }));
              await sleep(APPLY_BUSY_WAIT_MS);
              if (!isCurrent()) return;
              continue;
            }
          }
          // Anything else that is a definite refusal is not worth retrying.
          const retryable = !status || status >= 500;
          if (!retryable || attempt >= APPLY_RETRY_DELAYS_MS.length) {
            setApply((prev) => ({
              ...prev,
              status: "failed",
              notice: null,
              finishedAt: Date.now(),
              error: `${
                message || "A batch failed."
              } ${prev.scanned.toLocaleString(
                "en-GB"
              )} teams are already written and kept. Resume to continue from there.`,
            }));
            return;
          }
          const retryNotice = `Batch failed, retrying (${attempt + 1} of ${
            APPLY_RETRY_DELAYS_MS.length
          })…`;
          setApply((prev) => ({ ...prev, notice: retryNotice }));
          await sleep(APPLY_RETRY_DELAYS_MS[attempt]);
          attempt += 1;
          if (!isCurrent()) return;
        }
      }
      if (!isCurrent()) return;

      // The batch is committed: record its cursor before anything else can fail.
      afterTeamId = batch.next_after_team_id;
      const committedCursor = afterTeamId;
      setApply((prev) => ({
        ...prev,
        notice: null,
        scanned: prev.scanned + batch.scanned,
        inserted: prev.inserted + batch.inserted,
        updated: prev.updated + batch.updated,
        afterTeamId: committedCursor,
      }));

      if (afterTeamId) await sleep(applyPauseMs);
    } while (afterTeamId && isCurrent());

    if (!isCurrent()) return;
    setApply((prev) => ({
      ...prev,
      status: "done",
      notice: null,
      finishedAt: Date.now(),
    }));
  };

  const startApply = () => setConfirming("apply");

  const confirmApply = () => {
    setConfirming(null);
    runApply({ resume: false });
  };

  const stopApply = () => {
    applyIdRef.current += 1;
    setApply((prev) => ({
      ...prev,
      status: "stopped",
      notice: null,
      finishedAt: Date.now(),
    }));
  };

  const stopRun = () => {
    runIdRef.current += 1;
    setRun((prev) => ({ ...prev, status: "stopped", finishedAt: Date.now() }));
  };

  const handleClose = () => {
    if (applying) {
      setConfirming("close");
      return;
    }
    closeNow();
  };

  const closeNow = () => {
    setConfirming(null);
    // Leaving the dialog stops any batch loop still running.
    runIdRef.current += 1;
    applyIdRef.current += 1;
    if (applying) {
      setApply((prev) => ({
        ...prev,
        status: "stopped",
        notice: null,
        finishedAt: Date.now(),
      }));
    }
    if (running) {
      setRun((prev) => ({
        ...prev,
        status: "stopped",
        finishedAt: Date.now(),
      }));
    }
    onClose();
  };

  const filteredRows = useMemo(() => {
    const search = teamSearch.trim().toLowerCase();
    return rowsRef.current.filter((row) => {
      if (outcomeFilter === "changes" && row.outcome === "unchanged")
        return false;
      if (OUTCOMES.includes(outcomeFilter) && row.outcome !== outcomeFilter)
        return false;
      if (SKIP_REASONS[outcomeFilter] && row.skip_reason !== outcomeFilter)
        return false;
      if (
        search &&
        !row.fantasy_team_id.toLowerCase().includes(search) &&
        !(row.team_name || "").toLowerCase().includes(search) &&
        !(row.username || "").toLowerCase().includes(search)
      ) {
        return false;
      }
      return true;
    });
    // rowsVersion stands in for the ref's contents changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsVersion, outcomeFilter, teamSearch]);

  const pageRows = filteredRows.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE
  );

  const totalTeams = check.data?.total_teams ?? 0;
  const progress =
    totalTeams > 0 ? Math.min(100, (run.scanned / totalTeams) * 100) : 0;
  const elapsedSeconds = run.startedAt
    ? ((run.finishedAt ?? Date.now()) - run.startedAt) / 1000
    : 0;
  const remainingSeconds =
    running && run.scanned > 0
      ? (elapsedSeconds / run.scanned) * Math.max(totalTeams - run.scanned, 0)
      : null;

  const applyProgress =
    totalTeams > 0 ? Math.min(100, (apply.scanned / totalTeams) * 100) : 0;
  const applyElapsedSeconds = apply.startedAt
    ? ((apply.finishedAt ?? Date.now()) - apply.startedAt) / 1000
    : 0;

  const summaryChip = (key, label, color) => (
    <Chip
      key={key}
      label={`${label}: ${totals[key].toLocaleString("en-GB")}`}
      color={color}
      variant={outcomeFilter === key ? "filled" : "outlined"}
      onClick={() => {
        setOutcomeFilter(key);
        setPage(0);
      }}
      size="small"
    />
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: { width: "95vw", height: "92vh", maxWidth: "none" } }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
      >
        <Box sx={{ flexGrow: 1 }}>
          Re-calculate currency
          <Typography variant="body2" color="text.secondary">
            Works out every team's currency for a gameweek from its snapshot,
            the same way the live sync does. Re-calculate only reads; Apply
            writes the new and changed teams.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // Only the table may shrink; everything else keeps its height so
          // the pager below the table is never squeezed out of view.
          "& > :not(.MuiTableContainer-root)": { flexShrink: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Autocomplete
            options={gameweeks}
            loading={gameweeksLoading}
            value={gameweek}
            onChange={(event, value) => handleGameweekChange(value)}
            getOptionLabel={gameweekLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={running || applying}
            size="small"
            sx={{ minWidth: 320 }}
            renderInput={(params) => <TextField {...params} label="Gameweek" />}
          />
          {running ? (
            <Button variant="outlined" color="warning" onClick={stopRun}>
              Stop
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={!check.data || check.loading || applying}
              onClick={startRun}
            >
              Re-calculate
            </Button>
          )}
          {applying ? (
            <Button variant="outlined" color="warning" onClick={stopApply}>
              Stop writing
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="warning"
                // A finished apply makes the preview's counts stale; the
                // next Re-calculate re-enables it.
                disabled={
                  run.status !== "done" ||
                  totals.new + totals.changed === 0 ||
                  apply.status === "done"
                }
                onClick={startApply}
              >
                Apply
              </Button>
              {["failed", "stopped"].includes(apply.status) &&
                apply.afterTeamId && (
                  <Button
                    variant="outlined"
                    onClick={() => runApply({ resume: true })}
                  >
                    Resume
                  </Button>
                )}
            </>
          )}
          <TextField
            select
            label="Pause between writes"
            value={applyPauseMs}
            onChange={(e) => setApplyPauseMs(Number(e.target.value))}
            disabled={applying}
            size="small"
            sx={{ minWidth: 170 }}
          >
            {APPLY_PAUSES_MS.map((ms) => (
              <MenuItem key={ms} value={ms}>
                {ms / 1000}s
              </MenuItem>
            ))}
          </TextField>
          {check.loading && <CircularProgress size={20} />}
        </Box>

        {check.error && <Alert severity="error">{check.error}</Alert>}

        {check.data && (
          <Alert
            severity={check.data.existing_records > 0 ? "info" : "warning"}
          >
            {check.data.existing_records > 0
              ? `GW${
                  check.data.gameweek.number
                } has ${check.data.existing_records.toLocaleString(
                  "en-GB"
                )} currency records.`
              : `GW${check.data.gameweek.number} has no currency records yet; every eligible team will show as new.`}{" "}
            A full pass scans {check.data.total_teams.toLocaleString("en-GB")}{" "}
            teams in batches of 5,000. Best run outside match hours.
          </Alert>
        )}

        {run.status !== "idle" && (
          <Box>
            <LinearProgress
              variant={totalTeams > 0 ? "determinate" : "indeterminate"}
              value={progress}
              color={run.status === "failed" ? "error" : "primary"}
            />
            <Typography variant="caption" color="text.secondary">
              {run.scanned.toLocaleString("en-GB")} of{" "}
              {totalTeams.toLocaleString("en-GB")} teams
              {" · "}
              {formatSeconds(elapsedSeconds)} elapsed
              {remainingSeconds !== null &&
                ` · about ${formatSeconds(remainingSeconds)} left`}
              {run.status === "done" && " · complete"}
              {run.status === "stopped" && " · stopped"}
            </Typography>
          </Box>
        )}

        {run.error && <Alert severity="error">{run.error}</Alert>}

        {apply.status !== "idle" && (
          <Box>
            <LinearProgress
              variant={totalTeams > 0 ? "determinate" : "indeterminate"}
              value={applyProgress}
              color={apply.status === "failed" ? "error" : "warning"}
            />
            <Typography variant="caption" color="text.secondary">
              Writing: {apply.scanned.toLocaleString("en-GB")} of{" "}
              {totalTeams.toLocaleString("en-GB")} teams ·{" "}
              {apply.inserted.toLocaleString("en-GB")} inserted ·{" "}
              {apply.updated.toLocaleString("en-GB")} updated
              {" · "}
              {formatSeconds(applyElapsedSeconds)} elapsed
              {apply.status === "done" && " · complete"}
              {apply.status === "stopped" &&
                " · stopped (written batches kept)"}
            </Typography>
          </Box>
        )}

        {apply.notice && <Alert severity="info">{apply.notice}</Alert>}
        {apply.error && <Alert severity="error">{apply.error}</Alert>}
        {apply.status === "done" && (
          <Alert severity="success">
            Wrote GW{gameweek?.number}: {apply.inserted.toLocaleString("en-GB")}{" "}
            inserted and {apply.updated.toLocaleString("en-GB")} updated.
            Re-calculate again to confirm every team now shows as unchanged.
          </Alert>
        )}

        {run.status !== "idle" && (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Chip
                label="All changes"
                variant={outcomeFilter === "changes" ? "filled" : "outlined"}
                onClick={() => {
                  setOutcomeFilter("changes");
                  setPage(0);
                }}
                size="small"
              />
              {summaryChip("new", "New", outcomeColors.new)}
              {summaryChip("changed", "Changed", outcomeColors.changed)}
              {summaryChip("unchanged", "Unchanged", outcomeColors.unchanged)}
              {summaryChip("skipped", "Skipped", outcomeColors.skipped)}
              {Object.entries(SKIP_REASONS).map(([key, label]) =>
                summaryChip(key, label, "default")
              )}
              <TextField
                label="Find team, team ID or username"
                value={teamSearch}
                onChange={(e) => {
                  setTeamSearch(e.target.value);
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: 300, ml: "auto" }}
              />
              <TextField
                select
                label="Show"
                value={outcomeFilter}
                onChange={(e) => {
                  setOutcomeFilter(e.target.value);
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="changes">All changes</MenuItem>
                <MenuItem value="all">Every team</MenuItem>
                {OUTCOMES.map((outcome) => (
                  <MenuItem key={outcome} value={outcome}>
                    {outcome}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TableContainer
              component={Paper}
              variant="outlined"
              // Fills the space left and scrolls inside it, under a sticky header.
              sx={{ flex: "1 1 0", minHeight: 240, overflow: "auto" }}
            >
              <Table size="small" stickyHeader sx={compactTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Team</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Snapshot points</TableCell>
                    <TableCell align="right">Current amount</TableCell>
                    <TableCell align="right">New amount</TableCell>
                    <TableCell>Outcome</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {running
                            ? "Calculating…"
                            : "No teams match this view"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((row) => (
                      <TableRow key={row.fantasy_team_id} hover>
                        <TableCell>
                          <Box sx={{ fontSize: 12 }}>
                            {row.team_name || "—"}
                          </Box>
                          <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                            {row.fantasy_team_id}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ fontSize: 12 }}>{row.username || "—"}</Box>
                          <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                            {display(row.engagecraft_id)}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {display(row.snapshot_points)}
                        </TableCell>
                        <TableCell align="right">
                          {display(row.current_amount)}
                        </TableCell>
                        <TableCell align="right">
                          {display(row.new_amount)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              row.skip_reason
                                ? `${row.outcome}: ${
                                    SKIP_REASONS[row.skip_reason]
                                  }`
                                : row.outcome
                            }
                            color={outcomeColors[row.outcome] || "default"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[]}
              sx={{ "& .MuiTablePagination-displayedRows": { fontSize: 12 } }}
            />
          </>
        )}
      </DialogContent>

      <Dialog
        open={confirming === "apply"}
        onClose={() => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Write GW{gameweek?.number} currency?</DialogTitle>
        <DialogContent>
          <Box
            component="dl"
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 0.5,
              m: 0,
              "& dt": { color: "text.secondary" },
              "& dd": { m: 0, fontWeight: 600, textAlign: "right" },
            }}
          >
            <dt>New teams</dt>
            <dd>{totals.new.toLocaleString("en-GB")}</dd>
            <dt>Changed teams</dt>
            <dd>{totals.changed.toLocaleString("en-GB")}</dd>
            <dt>Teams to scan</dt>
            <dd>{totalTeams.toLocaleString("en-GB")}</dd>
            <dt>Pause between writes</dt>
            <dd>{applyPauseMs / 1000}s</dd>
          </Box>
          <DialogContentText sx={{ mt: 2, fontSize: 13 }}>
            Amounts are worked out again as each batch is written, and only new
            or changed teams are written. Each batch is saved on its own, so
            stopping keeps what is done. Written rows are sent to Gaming Hub
            once the currency switch is on.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmApply}>
            Write {(totals.new + totals.changed).toLocaleString("en-GB")} teams
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirming === "close"}
        onClose={() => setConfirming(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Stop writing and close?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13 }}>
            {apply.scanned.toLocaleString("en-GB")} teams are already written
            and kept. Running Apply for this gameweek again later skips them and
            continues safely.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Keep writing</Button>
          <Button variant="contained" color="warning" onClick={closeNow}>
            Stop and close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CurrencyRecalculatorDialog;
